import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import expenseReportsService from '../../services/expenseReportsService';
import { displayNameOf } from '../../utils/people';
import DateRangeFilter, { useDateRange } from './DateRangeFilter';
import ExpenseHistory from './ExpenseHistory';
import { exportExpenses } from './expenseExport';
import ExportMenu from './ExportMenu';
import PurposeCell from './PurposeCell';
import ReceiptCell from './ReceiptCell';
import ReceiptsModal from './ReceiptsModal';
import NoteEditModal from './NoteEditModal';
import ReviewActionModal, { ACTIONS } from './ReviewActionModal';
import { STATUS_META, errorMessage, fmtCents, fmtExpenseDate, latestNote, sumCents } from './helpers';

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved (to pay)' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
];

const keyOf = (expense) => `${expense.user_id}|${expense.expense_id}`;

const describeResult = (action, result) => {
  const done = `${ACTIONS[action].title}: ${result.updated} updated`;
  if (!result.failed) return { kind: 'success', text: `${done}.` };
  const reasons = [...new Set(result.results.filter((r) => !r.ok).map((r) => r.error))];
  return { kind: 'warning', text: `${done}, ${result.failed} not changed — ${reasons.join(' ')}` };
};

/**
 * The approver's view: everyone's expenses, what is owed to whom, and the
 * approve / reject / mark paid / note actions. Approvers can act on their own
 * expenses too; those are marked "You".
 */
const ReviewPanel = () => {
  const auth = useContext(AuthContext);
  const myId = auth?.user?.user_id;

  const [statusFilter, setStatusFilter] = useState('pending');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const { selection: rangeSelection, update: updateRange, range, error: rangeError } = useDateRange();
  // Expenses in the chosen status that the date range leaves out.
  const [outsideCount, setOutsideCount] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(null);
  // { action, items, description? } while the confirm dialog is open
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  // { expense, index, entry } while a note is being edited
  const [noteEdit, setNoteEdit] = useState(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [receiptsFor, setReceiptsFor] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await expenseReportsService.getAll(statusFilter, range);
      setExpenses(data.expenses);
      setTotals(data.totals);
      setOutsideCount(data.outside_range_count || 0);
      setSelected(new Set());
    } catch (err) {
      setError(`Couldn't load expenses: ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, range]);

  useEffect(() => {
    load();
  }, [load]);

  const employees = useMemo(() => {
    const names = new Map();
    expenses.forEach((e) => names.set(e.user_id, e.employee_name));
    return [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [expenses]);

  const visible = useMemo(
    () => (employeeFilter ? expenses.filter((e) => e.user_id === employeeFilter) : expenses),
    [expenses, employeeFilter]
  );

  const handleExport = (format) => {
    const statusLabel = statusFilter === 'all' ? 'All statuses' : STATUS_META[statusFilter].label;
    const employeeLabel = employeeFilter
      ? employees.find(([id]) => id === employeeFilter)?.[1] || 'Selected employee'
      : 'All employees';
    return exportExpenses(visible, {
      range,
      statusKey: statusFilter,
      statusLabel,
      employeeLabel,
      preparedBy: displayNameOf(auth?.user) || '',
    }, format);
  };

  const isOwn = (expense) => Boolean(myId) && expense.user_id === myId;
  const selectedItems = visible.filter((e) => selected.has(keyOf(e)));
  const allSelected = visible.length > 0 && visible.every((e) => selected.has(keyOf(e)));

  const toggle = (expense) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = keyOf(expense);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(visible.map(keyOf)));
  };

  const openAction = (action, items, description) => {
    setActionError('');
    setPending({ action, items, description });
  };

  const markAllPaid = async (total) => {
    try {
      setError('');
      // Fetch the approved list fresh so the dialog shows exactly what will be paid.
      const data = await expenseReportsService.getAll('approved');
      const items = data.expenses.filter((e) => e.user_id === total.user_id);
      if (!items.length) {
        setNotice({ kind: 'info', text: `Nothing approved is waiting to be paid to ${total.employee_name}.` });
        await load();
        return;
      }
      openAction(
        'mark_paid',
        items,
        <>
          Mark <strong>{items.length}</strong> approved {items.length === 1 ? 'expense' : 'expenses'} for{' '}
          <strong>{total.employee_name}</strong> as paid, totalling <strong>{fmtCents(sumCents(items))}</strong>.
        </>
      );
    } catch (err) {
      setError(`Couldn't load approved expenses: ${errorMessage(err)}`);
    }
  };

  const confirmAction = async (note) => {
    try {
      setBusy(true);
      setActionError('');
      const result = await expenseReportsService.review(pending.items, pending.action, note);
      setNotice(describeResult(pending.action, result));
      setPending(null);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Swap in the expense the service returned, so an open history stays open.
  const replaceExpense = (updated) => {
    setExpenses((prev) => prev.map((e) => (keyOf(e) === keyOf(updated) ? updated : e)));
  };

  // A 409 means the note changed under us: say so and show the current state.
  const noteFailed = async (err) => {
    if (err?.response?.status === 409) {
      setNoteEdit(null);
      // After the reload, which clears the error banner when it starts.
      await load();
      setError(errorMessage(err));
      return;
    }
    if (noteEdit) setNoteError(errorMessage(err));
    else setError(`Couldn't change the note: ${errorMessage(err)}`);
  };

  const saveNote = async (text) => {
    try {
      setNoteBusy(true);
      setNoteError('');
      replaceExpense(await expenseReportsService.changeNote(noteEdit.expense, noteEdit.index, noteEdit.entry, 'edit', text));
      setNoteEdit(null);
    } catch (err) {
      await noteFailed(err);
    } finally {
      setNoteBusy(false);
    }
  };

  const deleteNote = async (expense, index, entry) => {
    if (!window.confirm(`Delete the note “${entry.note}”? Approvers can still see it under earlier versions.`)) return;
    try {
      setError('');
      replaceExpense(await expenseReportsService.changeNote(expense, index, entry, 'delete'));
    } catch (err) {
      await noteFailed(err);
    }
  };

  const can = (status) => selectedItems.length > 0 && selectedItems.every((e) => status.includes(e.status));
  const owedTotal = totals.reduce((sum, t) => sum + t.owed_cents, 0);

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
      {notice && (
        <div className={`alert alert-${notice.kind} alert-dismissible`} role="status">
          {notice.text}
          <button type="button" className="btn-close" onClick={() => setNotice(null)} aria-label="Close"></button>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="bi bi-cash-stack me-2"></i>
            Owed by employee
          </h5>
          <span className="fw-semibold" data-testid="owed-total">Total owed: {fmtCents(owedTotal)}</span>
        </div>
        <div className="card-body">
          {totals.length === 0 ? (
            <p className="text-muted mb-0">{loading ? 'Loading…' : 'Nothing is pending or waiting to be paid.'}</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="text-end">Approved, to pay</th>
                    <th className="text-end">Waiting for review</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {totals.map((total) => (
                    <tr key={total.user_id}>
                      <td>
                        {total.employee_name}
                        {total.employee_email && total.employee_email !== total.employee_name && (
                          <div className="text-muted small">{total.employee_email}</div>
                        )}
                      </td>
                      <td className="text-end">
                        <strong>{fmtCents(total.owed_cents)}</strong>
                        <span className="text-muted small"> ({total.owed_count})</span>
                      </td>
                      <td className="text-end">
                        {fmtCents(total.pending_cents)}
                        <span className="text-muted small"> ({total.pending_count})</span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => markAllPaid(total)}
                          disabled={!total.owed_count}
                        >
                          <i className="bi bi-cash-coin me-1"></i>
                          Mark all paid
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="bi bi-list-check me-2"></i>
              Expenses ({visible.length})
            </h5>
            <div className="d-flex flex-wrap gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setEmployeeFilter('');
                }}
                aria-label="Status"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                aria-label="Employee"
              >
                <option value="">All employees</option>
                {employees.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <DateRangeFilter selection={rangeSelection} onChange={updateRange} error={rangeError} idPrefix="reviewRange" />
              <ExportMenu onExport={handleExport} disabled={loading || visible.length === 0} />
            </div>
          </div>
          {outsideCount > 0 && (
            <div className="small text-muted mt-2" role="status">
              {outsideCount} {statusFilter === 'all' ? '' : `${STATUS_META[statusFilter].label.toLowerCase()} `}
              {outsideCount === 1 ? 'expense falls' : 'expenses fall'} outside this date range.{' '}
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => updateRange({ key: 'all' })}>
                Show all dates
              </button>
            </div>
          )}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
            <span className="text-muted small me-2">
              {selectedItems.length
                ? `${selectedItems.length} selected · ${fmtCents(sumCents(selectedItems))}`
                : 'Select expenses to act on them'}
            </span>
            <button className="btn btn-success btn-sm" disabled={!can(['pending', 'rejected'])} onClick={() => openAction('approve', selectedItems)}>
              <i className="bi bi-check-circle me-1"></i>Approve
            </button>
            <button className="btn btn-danger btn-sm" disabled={!can(['pending', 'approved'])} onClick={() => openAction('reject', selectedItems)}>
              <i className="bi bi-x-circle me-1"></i>Reject
            </button>
            <button className="btn btn-primary btn-sm" disabled={!can(['approved'])} onClick={() => openAction('mark_paid', selectedItems)}>
              <i className="bi bi-cash-coin me-1"></i>Mark paid
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled={!selectedItems.length} onClick={() => openAction('note', selectedItems)}>
              <i className="bi bi-chat-left-text me-1"></i>Add note
            </button>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4 d-block mb-3"></i>
              No expenses to show.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={!visible.length}
                        aria-label="Select all"
                      />
                    </th>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Business purpose</th>
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th>Receipts</th>
                    <th>Latest note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((expense) => {
                    const key = keyOf(expense);
                    const own = isOwn(expense);
                    const note = latestNote(expense);
                    const meta = STATUS_META[expense.status] || { label: expense.status, badge: 'text-bg-secondary' };
                    const isOpen = expanded === key;
                    return (
                      <Fragment key={key}>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selected.has(key)}
                              onChange={() => toggle(expense)}
                              aria-label={`Select ${expense.employee_name} ${expense.merchant}`}
                            />
                          </td>
                          <td>
                            {expense.employee_name}
                            {own && <span className="badge text-bg-light border ms-1">You</span>}
                          </td>
                          <td className="text-nowrap">{fmtExpenseDate(expense.expense_date)}</td>
                          <td>{expense.category}</td>
                          <td>{expense.merchant}</td>
                          <PurposeCell expense={expense} maxWidth={260} />
                          <td className="text-end text-nowrap">{fmtCents(expense.amount_cents)}</td>
                          <td><span className={`badge ${meta.badge}`}>{meta.label}</span></td>
                          <ReceiptCell expense={expense} />
                          <td className="small" style={{ maxWidth: 220 }}>
                            {note ? <span className="fst-italic">“{note.note}”</span> : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-nowrap">
                            <button
                              className="btn btn-link btn-sm"
                              onClick={() => setExpanded(isOpen ? null : key)}
                              aria-label={isOpen ? 'Hide history' : 'Show history'}
                              title="History"
                            >
                              <i className="bi bi-clock-history"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setReceiptsFor(expense)}
                              aria-label={`Receipts for ${expense.merchant}`}
                            >
                              <i className="bi bi-paperclip me-1" aria-hidden="true"></i>
                              Receipts
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="table-light">
                            <td colSpan={11}>
                              <ExpenseHistory
                                history={expense.history}
                                onEditNote={(index, entry) => {
                                  setNoteError('');
                                  setNoteEdit({ expense, index, entry });
                                }}
                                onDeleteNote={(index, entry) => deleteNote(expense, index, entry)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {receiptsFor && (
        <ReceiptsModal
          expense={receiptsFor}
          mode="review"
          onClose={() => setReceiptsFor(null)}
          onSaved={(updated) => {
            replaceExpense(updated);
            setReceiptsFor(null);
          }}
        />
      )}

      {noteEdit && (
        <NoteEditModal
          entry={noteEdit.entry}
          onSave={saveNote}
          onClose={() => setNoteEdit(null)}
          busy={noteBusy}
          error={noteError}
        />
      )}

      {pending && (
        <ReviewActionModal
          action={pending.action}
          count={pending.items.length}
          cents={sumCents(pending.items)}
          description={pending.description}
          onConfirm={confirmAction}
          onClose={() => setPending(null)}
          busy={busy}
          error={actionError}
        />
      )}
    </>
  );
};

export default ReviewPanel;
